import React, { useState } from 'react';
import apiService from '../../services/apiService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { ArrowLeft, ArrowRight, Loader2, Gift, Check } from 'lucide-react';

interface SiteSetupStepProps {
  onNext: (data: { siteName: string; siteDomain: string; siteId: string }) => void;
  onBack: () => void;
  initialData?: {
    siteName?: string;
    siteDomain?: string;
    siteId?: string;
  };
}

const SiteSetupStep: React.FC<SiteSetupStepProps> = ({
  onNext,
  onBack,
  initialData,
}) => {
  const [siteName, setSiteName] = useState(initialData?.siteName || '');
  const [siteDomain, setSiteDomain] = useState(initialData?.siteDomain || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useSampleData, setUseSampleData] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (useSampleData) {
      await createSampleSite();
    } else {
      await createSite();
    }
  };

  const createSite = async () => {
    try {
      setLoading(true);

      const response = await apiService.post('/sites', {
        name: siteName,
        domain: siteDomain,
        defaultLanguage: 'en',
        enabledLanguages: ['en'],
      });

      onNext({
        siteName,
        siteDomain,
        siteId: response.data.site.id,
      });
    } catch (err: any) {
      console.error('Failed to create site:', err);
      setError(err.response?.data?.error?.message || 'Failed to create site');
    } finally {
      setLoading(false);
    }
  };

  const createSampleSite = async () => {
    try {
      setLoading(true);

      const response = await apiService.post('/onboarding/sample-data');

      onNext({
        siteName: 'My Sample E-commerce Store',
        siteDomain: 'sample-store.example.com',
        siteId: response.data.data.siteId,
      });
    } catch (err: any) {
      console.error('Failed to create sample site:', err);
      setError(err.response?.data?.error?.message || 'Failed to create sample site');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CardContent className="p-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full text-3xl mb-4">
            🌐
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Create Your First Site
          </h2>
          <p className="text-muted-foreground">
            Let's set up a website or app for localization
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Sample Data Option */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setUseSampleData(!useSampleData)}
            className={cn(
              "w-full bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-6 border-2 text-left transition-all",
              useSampleData ? "border-primary" : "border-primary/20 hover:border-primary/40"
            )}
          >
            <div className="flex items-start">
              <div className={cn(
                "h-5 w-5 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 mt-0.5",
                useSampleData ? "bg-primary border-primary" : "border-muted-foreground"
              )}>
                {useSampleData && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-1">
                  <span className="font-semibold text-foreground">
                    Use sample e-commerce store
                  </span>
                  <Badge className="ml-2 bg-primary text-primary-foreground">
                    Recommended
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  We'll create a sample e-commerce site with 10 pre-filled translations,
                  a glossary, and example content. Perfect for testing!
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {!useSampleData && (
            <>
              {/* Site Name */}
              <div className="space-y-2">
                <Label htmlFor="siteName">Site Name *</Label>
                <Input
                  type="text"
                  id="siteName"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="e.g., My Online Store"
                  required={!useSampleData}
                />
                <p className="text-sm text-muted-foreground">
                  A friendly name to identify this site
                </p>
              </div>

              {/* Site Domain */}
              <div className="space-y-2">
                <Label htmlFor="siteDomain">Domain *</Label>
                <Input
                  type="text"
                  id="siteDomain"
                  value={siteDomain}
                  onChange={(e) => setSiteDomain(e.target.value)}
                  placeholder="e.g., mystore.com"
                  required={!useSampleData}
                />
                <p className="text-sm text-muted-foreground">
                  Your website domain (without https://)
                </p>
              </div>
            </>
          )}

          {useSampleData && (
            <Card className="bg-primary/5 border-0">
              <CardContent className="p-6 text-center">
                <Gift className="h-12 w-12 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-foreground mb-2">
                  Sample Site Includes:
                </h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-center justify-center">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    10 pre-filled content keys
                  </li>
                  <li className="flex items-center justify-center">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    Sample translations (Spanish, French, German)
                  </li>
                  <li className="flex items-center justify-center">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    E-commerce glossary with common terms
                  </li>
                  <li className="flex items-center justify-center">
                    <Check className="h-4 w-4 text-green-500 mr-2" />
                    Ready to explore immediately
                  </li>
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-6 border-t">
            <Button type="button" variant="ghost" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <Button
              type="submit"
              disabled={loading || (!useSampleData && (!siteName || !siteDomain))}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </CardContent>
  );
};

export default SiteSetupStep;
